import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { ListTable } from '../components/list-table.js';
import type { QueueSectionProps } from '../lib/approval-queue-section.js';
import {
  decideExportRequest,
  fetchExportRequests,
  type ExportRequestDto,
} from '../lib/connectors.js';

/**
 * Krok SR1 bod 5: sekce „Export na portály ke schválení" na existujici
 * obrazovce Schvaleni (stejny princip jako A5 reporty a A4 dotazy — ne nova
 * obrazovka, ne nova polozka v menu).
 *
 * Schvaleni znamena, ze operaci zaradi SCHVALOVATEL pod svym principalem;
 * nováček tedy nikdy nema vlastni cestu na portal.
 *
 * Stav si sekce drzi sama (`useState` + `fetch`), stejne jako sousedni fronty
 * — stranka Schvaleni nebezi v `QueryClientProvider`.
 */
export function ExportApprovalQueue({ inQueue = false, onCount }: QueueSectionProps = {}): React.JSX.Element | null {
  const [items, setItems] = React.useState<readonly ExportRequestDto[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      setItems(await fetchExportRequests('pending'));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  /* Pocet hlasi stranka vys, az kdyz je znamy — `null` je „jeste nevim". */
  React.useEffect(() => {
    if (items !== null) onCount?.(items.length);
  }, [items, onCount]);

  const decide = (id: string, decision: 'release' | 'reject'): void => {
    setBusyId(id);
    setError(null);
    void decideExportRequest(id, decision)
      .then(() => load())
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : String(cause)))
      .finally(() => setBusyId(null));
  };

  // Dokud nic neceka, sekce se vubec nezobrazuje (jako sousedni fronty).
  if (items === null || items.length === 0) return null;

  /**
   * Popisek operace z VYCTU, ne z dynamickeho i18n klice: neznamou hodnotu
   * z API nikdy nepustime do `t()` (jinak by chybejici klic shodil celou
   * stranku Schvaleni, kde tahle sekce jen bydli).
   */
  const operationLabel = (operation: string): string => {
    const labels: Readonly<Record<string, MessageKey>> = {
      PUBLISH: 'webAdmin.portaly.enqueueTest',
      UPDATE: 'webAdmin.portaly.sr1.operation.UPDATE',
      WITHDRAW: 'webAdmin.portaly.sr1.operation.WITHDRAW',
      RENEW: 'webAdmin.portaly.sr1.operation.RENEW',
      TOP: 'webAdmin.portaly.sr1.operation.TOP',
    };
    const key = labels[operation];
    return key === undefined ? operation : t(key, { locale: 'cs' });
  };

  return (
    <section className="mb-6" data-testid="export-approval-queue">
      {/* Krok UXF5 bod 4: v jedne fronte „Čeká na rozhodnutí (N)" nadpis nestoji. */}
      {inQueue ? null : (
        <h2 className="text-lg font-semibold mb-2">
          {t('webAdmin.portaly.sr1.approvalsHeading', { locale: 'cs' })}
        </h2>
      )}
      {error !== null ? (
        <p className="text-sm mb-2" role="alert" style={{ color: 'var(--rh-danger)' }}>
          {error}
        </p>
      ) : null}
      <ListTable
        testId="export-approvals"
        caption={t('webAdmin.portaly.sr1.approvalsHeading', { locale: 'cs' })}
        rows={items}
        rowKey={(item) => item.id}
        empty={<span>{t('webAdmin.portaly.sr1.approvalsEmpty', { locale: 'cs' })}</span>}
        columns={[
          {
            key: 'portal',
            label: t('webAdmin.portaly.sr1.approvalsColumn.portal', { locale: 'cs' }),
            cell: (item: ExportRequestDto) => item.portal,
          },
          {
            key: 'operation',
            label: t('webAdmin.portaly.sr1.approvalsColumn.operation', { locale: 'cs' }),
            cell: (item) => operationLabel(String(item.operation)),
          },
          {
            key: 'requestedBy',
            label: t('webAdmin.portaly.sr1.approvalsColumn.requestedBy', { locale: 'cs' }),
            // Zkracene id zadatele — stejne jako ve fronte schvaleni nastroju.
            cell: (item) => <span className="text-xs">{String(item.requestedBy).slice(0, 8)}</span>,
          },
          {
            key: 'actions',
            label: t('webAdmin.portaly.sr1.approvalsColumn.actions', { locale: 'cs' }),
            srOnlyLabel: true,
            cell: (item) => (
              <>
                <button
                  type="button"
                  className="rh-a2-btn rh-a2-btn--sm rh-a2-btn--primary mr-1"
                  disabled={busyId === item.id}
                  onClick={() => decide(item.id, 'release')}
                  data-testid={`export-approve-${item.id}`}
                >
                  {t('webAdmin.portaly.sr1.approve', { locale: 'cs' })}
                </button>
                <button
                  type="button"
                  className="rh-a2-btn rh-a2-btn--sm rh-a2-btn--danger"
                  disabled={busyId === item.id}
                  onClick={() => decide(item.id, 'reject')}
                  data-testid={`export-reject-${item.id}`}
                >
                  {t('webAdmin.portaly.sr1.reject', { locale: 'cs' })}
                </button>
              </>
            ),
          },
        ]}
      />
    </section>
  );
}
